import { Injectable, computed, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, tap } from 'rxjs';

import { environment } from '../../../environments/environment';
import { CurrentUser, PortalRole, TokenResponse } from '../models';
import { OFFICE_DASHBOARD_CODES } from '../util/dashboard-access';

const ACCESS_KEY = 'schoolos.access_token';
const REFRESH_KEY = 'schoolos.refresh_token';
const USER_KEY = 'schoolos.user';
const BRANCH_KEY = 'schoolos.active_branch';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);

  private readonly _user = signal<CurrentUser | null>(this.restoreUser());
  /** The branch a school-wide admin is currently acting in. Null for everyone
   *  else — a branch admin's branch is fixed on their account. */
  private readonly _activeBranchId = signal<number | null>(this.restoreBranch());

  readonly user = this._user.asReadonly();
  readonly activeBranchId = this._activeBranchId.asReadonly();
  readonly isAuthenticated = computed(() => this._user() !== null);
  readonly role = computed<PortalRole | null>(() => this._user()?.role ?? null);
  readonly permissions = computed<Set<string>>(() => new Set(this._user()?.permissions ?? []));

  /**
   * The branch every request should be scoped to. For a school-wide admin
   * this is the branch they have chosen — never `user.branch_id`, which the
   * server fills in from the last request's header and would otherwise stick.
   * For everyone else it is the branch fixed on their account.
   */
  readonly effectiveBranchId = computed<number | null>(() => {
    const u = this._user();
    if (u?.role === 'SCHOOL_ADMIN') {
      return this._activeBranchId();
    }
    return u?.branch_id ?? null;
  });

  /** A school-wide admin who has not yet picked a branch to work in. Until
   *  they do, branch-scoped screens have nothing to show. */
  readonly needsBranchSelection = computed<boolean>(
    () => this._user()?.role === 'SCHOOL_ADMIN' && this._activeBranchId() === null
  );

  private restoreBranch(): number | null {
    try {
      const raw = localStorage.getItem(BRANCH_KEY);
      return raw ? Number(raw) : null;
    } catch {
      return null;
    }
  }

  /** Set (or clear) the branch a school-wide admin is working in. */
  setActiveBranch(branchId: number | null): void {
    try {
      if (branchId === null) {
        localStorage.removeItem(BRANCH_KEY);
      } else {
        localStorage.setItem(BRANCH_KEY, String(branchId));
      }
    } catch {
      // Non-fatal: the choice simply will not survive a reload.
    }
    this._activeBranchId.set(branchId);
  }

  private restoreUser(): CurrentUser | null {
    try {
      const raw = localStorage.getItem(USER_KEY);
      return raw ? (JSON.parse(raw) as CurrentUser) : null;
    } catch {
      return null;
    }
  }

  get accessToken(): string | null {
    try {
      return localStorage.getItem(ACCESS_KEY);
    } catch {
      return null;
    }
  }

  get refreshToken(): string | null {
    try {
      return localStorage.getItem(REFRESH_KEY);
    } catch {
      return null;
    }
  }

  /** True when the signed-in user holds this permission. */
  can(permission: string): boolean {
    return this.permissions().has(permission);
  }

  /**
   * Whether the school's plan includes this role at all. A switched-off role
   * cannot sign in, so the screens do not offer to issue it a login.
   */
  roleEnabled(role: string): boolean {
    return !(this._user()?.disabled_roles ?? []).includes(role);
  }

  login(username: string, password: string): Observable<TokenResponse> {
    return this.http
      .post<TokenResponse>(`${environment.apiUrl}/auth/login`, { username, password })
      .pipe(
        tap(response => {
          // A fresh sign-in starts with no branch chosen — a stale choice from
          // a previous session must not leak into a different login. (Token
          // refresh, which also calls store(), deliberately keeps it.)
          this.setActiveBranch(null);
          this.store(response);
        })
      );
  }

  refresh(): Observable<TokenResponse> {
    return this.http
      .post<TokenResponse>(`${environment.apiUrl}/auth/refresh`, {
        refresh_token: this.refreshToken
      })
      .pipe(tap(response => this.store(response)));
  }

  /** True while this account is still using a password an administrator set. */
  readonly mustChangePassword = computed<boolean>(
    () => this._user()?.must_change_password === true
  );

  /**
   * Re-read the signed-in user from the server.
   *
   * Used after a password change: the stored session still says a change is
   * pending, and only the server can say it no longer is.
   */
  refreshUser(): Observable<CurrentUser> {
    return this.http.get<CurrentUser>(`${environment.apiUrl}/auth/me`).pipe(
      tap(user => {
        try {
          localStorage.setItem(USER_KEY, JSON.stringify(user));
        } catch {
          // Non-fatal: the session simply will not survive a reload.
        }
        this._user.set(user);
      })
    );
  }

  /**
   * @param reason Shown on the login screen after the redirect — for a logout
   * the person did not click themselves, silence would read as a bug.
   */
  logout(redirect = true, reason?: string): void {
    try {
      localStorage.removeItem(ACCESS_KEY);
      localStorage.removeItem(REFRESH_KEY);
      localStorage.removeItem(USER_KEY);
      localStorage.removeItem(BRANCH_KEY);
    } catch {
      // Storage can be unavailable; clearing in-memory state is what matters.
    }
    this._user.set(null);
    this._activeBranchId.set(null);
    if (redirect) {
      this.router.navigate(['/login'], reason ? { queryParams: { reason } } : undefined);
    }
  }

  /**
   * Office screens in the order an office role would want to land on them,
   * each with the permissions that open it — space-separated when every one
   * is needed, `|`-separated when any one will do. The office roles all share the
   * `/school-admin` area but a school decides what each may do, so "home" is
   * the first of these the person can actually open rather than a fixed page
   * — a fixed page the school has switched off would bounce them back to
   * itself for ever.
   */
  private static readonly OFFICE_HOMES: Partial<Record<PortalRole, [string, string][]>> = {
    ACCOUNTANT: [['/school-admin/fees', 'fee.read'], ['/school-admin/students', 'student.read']],
    FRONT_OFFICE: [['/school-admin/students', 'student.read'], ['/school-admin/classes', 'class.read']],
    EXAM_COORDINATOR: [['/school-admin/exams', 'exam.read'], ['/school-admin/results', 'result.view']]
  };

  private static readonly OFFICE_FALLBACKS: [string, string][] = [
    ['/school-admin/dashboard', OFFICE_DASHBOARD_CODES.join('|')],
    ['/school-admin/students', 'student.read'],
    ['/school-admin/exams', 'exam.read'],
    ['/school-admin/fees', 'fee.read'],
    ['/announcements', 'announcement.read']
  ];

  /** Where a role lands after signing in. */
  homeRoute(role: PortalRole | null = this.role()): string {
    switch (role) {
      case 'SUPER_ADMIN':
        return '/dashboard';
      case 'TEACHER':
        return '/teacher/dashboard';
      case 'PARENT':
        return '/parent/dashboard';
      case 'STUDENT':
        return '/student/dashboard';
      case 'SCHOOL_ADMIN':
      case 'BRANCH_ADMIN':
      case 'PRINCIPAL':
      case 'ACCOUNTANT':
      case 'FRONT_OFFICE':
      case 'EXAM_COORDINATOR': {
        const candidates = [...(AuthService.OFFICE_HOMES[role] ?? []), ...AuthService.OFFICE_FALLBACKS];
        const opens = (codes: string) => codes.includes('|')
          ? codes.split('|').some(code => this.can(code))
          : codes.split(' ').every(code => this.can(code));
        const found = candidates.find(([, codes]) => opens(codes));
        return found ? found[0] : '/profile';
      }
      default:
        // Every signed-in role holds self.profile; landing on /login instead
        // would send a signed-in person straight back here in a loop.
        return '/profile';
    }
  }

  /**
   * Path prefixes that belong to one portal and nobody else — `/school-admin`
   * for the office, `/teacher` for a teacher, and so on. `/dashboard`,
   * `/schools` and `/audit-logs` are the platform's own, unprefixed routes,
   * so they are named directly rather than sharing a prefix with anything.
   * Anything not listed here (`/timetable`, `/announcements`, `/tasks`, …) is
   * a screen every portal can reach, so it is never role-checked.
   */
  private static readonly EXCLUSIVE_PREFIXES: { prefix: string; roles: PortalRole[] }[] = [
    { prefix: '/dashboard', roles: ['SUPER_ADMIN'] },
    { prefix: '/schools', roles: ['SUPER_ADMIN'] },
    { prefix: '/audit-logs', roles: ['SUPER_ADMIN'] },
    // The parent directory is platform-only; a school reaches a parent through
    // the child's own page rather than browsing a list of families.
    { prefix: '/parents', roles: ['SUPER_ADMIN'] },
    // The office area — the school and branch admins and the office staff share it.
    {
      prefix: '/school-admin',
      roles: ['SCHOOL_ADMIN', 'BRANCH_ADMIN', 'PRINCIPAL', 'ACCOUNTANT', 'FRONT_OFFICE', 'EXAM_COORDINATOR']
    },
    { prefix: '/teacher', roles: ['TEACHER'] },
    { prefix: '/parent', roles: ['PARENT'] },
    { prefix: '/student', roles: ['STUDENT'] }
  ];

  /**
   * Where to send someone right after they sign in.
   *
   * A `returnUrl` on the login screen means somebody followed a link (or was
   * bounced there by the auth guard) before they had signed in — often
   * before *this* browser had signed in as anyone at all, so the route it
   * names may belong to a different portal than the account that just
   * authenticated. Honouring it blindly would land a teacher on the platform
   * dashboard because the previous tab happened to have that open. So it is
   * followed only when it belongs to the portal this role actually has;
   * everywhere else, the role's own home route wins.
   */
  resolveRedirect(returnUrl: string | null, role: PortalRole | null): string {
    if (!returnUrl) {
      return this.homeRoute(role);
    }
    if (!this.isPathAllowedForRole(returnUrl, role)) {
      return this.homeRoute(role);
    }
    return returnUrl;
  }

  /**
   * Whether `role` may sit at `path` at all — the same rule `resolveRedirect`
   * uses for a fresh sign-in, reused for someone already signed in who
   * navigates (a typed URL, an old bookmark, a stale link) into a path that
   * belongs to a different portal.
   */
  isPathAllowedForRole(path: string, role: PortalRole | null): boolean {
    const bare = path.split('?')[0].split('#')[0];
    const exclusive = AuthService.EXCLUSIVE_PREFIXES.find(
      e => bare === e.prefix || bare.startsWith(`${e.prefix}/`)
    );
    return !exclusive || (role !== null && exclusive.roles.includes(role));
  }

  private store(response: TokenResponse): void {
    try {
      localStorage.setItem(ACCESS_KEY, response.access_token);
      localStorage.setItem(REFRESH_KEY, response.refresh_token);
      localStorage.setItem(USER_KEY, JSON.stringify(response.user));
    } catch {
      // Non-fatal: the session simply will not survive a reload.
    }
    this._user.set(response.user);
  }
}
